/**
 * Quiz Popup Stub Component
 * 
 * Manages the quiz popup/modal and dynamically loads quiz content via Sections API.
 * Works with the quiz-popup section and quiz-content section.
 */

import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { DialogCloseEvent } from '@theme/dialog';
import { QuizResultsRenderer } from '@theme/quiz-results';

class QuizPopupStub extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.quizUrl = this.dataset.quizUrl;
    this.colorScheme = this.dataset.colorScheme || 'scheme-1';
    this.preloaded = false;
    // Support both data-dynamic-content and direct content rendering
    this.dynamicContent = this.querySelector('[data-dynamic-content]') || this.querySelector('.quiz-modal__content');
    this.dialogComponent = this.querySelector('dialog-component');
    this.originalQuizHTML = null; // Store original quiz HTML for restart
    this.quizHTMLBeforeEmail = null; // Store quiz HTML before showing email capture (for back button)
    this.isEmailSubmitting = false; // Flag to prevent double submission on email form
    this.originalBackButtonHTML = null; // Store original back button HTML for restart
    
    if (!this.dynamicContent) {
      console.error('Quiz popup stub missing content container');
      return;
    }
        // Result rendering is shared with the standalone quiz page - see @theme/quiz-results.
    this.renderer = new QuizResultsRenderer({
      container: /** @type {HTMLElement} */ (this.dynamicContent),
      config: this.dataset,
      getBackButton: () => /** @type {HTMLElement|null} */ (document.querySelector('[data-quiz-back]')),
      onRestart: () => this.handleQuizRestart(),
      onAddToCart: () => this.dialogComponent?.closeDialog(),
    });
    
    // Check if content is already rendered (not using URL-based loading)
    if (!this.quizUrl && this.dynamicContent.querySelector('quiz-component')) {
      this.preloaded = true;
      // Store original HTML for restart functionality
      this.originalQuizHTML = this.dynamicContent.innerHTML;
      this.initializeQuizComponent();
    }
    
    // Listen for dialog close events to hide the stub
    // Note: We show the stub directly in initializePopup() when opening
    const handleDialogClose = () => {
      this.style.display = 'none';
    };
    
    document.addEventListener(DialogCloseEvent.eventName, handleDialogClose);
    this.handleDialogClose = handleDialogClose;
    
    this.setupTriggers();
  }

  /**
   * Setup trigger buttons that open this quiz popup
   */
  setupTriggers() {
    // Find all trigger buttons
    const triggers = document.querySelectorAll('[data-quiz-trigger]');
    
    triggers.forEach((trigger) => {
      // Skip if already initialized
      if (trigger.dataset.quizTriggerInitialized) {
        return;
      }
      
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.initializePopup(trigger);
      });
      
      // Preload on hover/focus for better UX
      trigger.addEventListener('mouseenter', () => {
        this.preload();
      });
      trigger.addEventListener('focus', () => {
        this.preload();
      });
      
      trigger.dataset.quizTriggerInitialized = 'true';
    });
  }

  /**
   * Preload quiz content before user opens the popup
   */
  async preload() {
    if (this.preloaded) return;
    
    // If content is already rendered, mark as preloaded
    if (!this.quizUrl && this.dynamicContent.querySelector('quiz-component')) {
      this.preloaded = true;
      this.initializeQuizComponent();
      return;
    }
    
    // Otherwise load from URL if provided
    if (this.quizUrl) {
    await this.getQuizContent();
    this.preloaded = true;
    }
  }

  /**
   * Initialize and open the popup
   */
  async initializePopup(opener) {
    // Ensure dialog component is ready
    if (!this.dialogComponent) {
      this.dialogComponent = this.querySelector('dialog-component');
    }
    
    if (!this.dialogComponent) {
      console.error('Quiz popup: dialog-component not found');
      return;
    }
    
    // Wait for dialog component to be fully initialized
    const dialogComponent = /** @type {any} */ (this.dialogComponent);
    if (!dialogComponent.refs || !dialogComponent.refs.dialog) {
      // Wait a bit for the component to initialize
      await new Promise(resolve => setTimeout(resolve, 10));
      if (!dialogComponent.refs || !dialogComponent.refs.dialog) {
        console.error('Quiz popup: dialog-component not ready');
        return;
      }
    }
    
    // If content is already rendered, just open the dialog
    if (this.preloaded && this.dynamicContent && this.dynamicContent.querySelector('quiz-component')) {
      this.initializeQuizComponent();
    } else if (this.quizUrl) {
      // Load content from URL if provided
    await this.getQuizContent();
    } else {
      // Content should already be rendered, just ensure it's initialized
      this.preloaded = true;
      this.initializeQuizComponent();
    }
    
    if (typeof dialogComponent.showDialog === 'function') {
      // Show the quiz popup stub before opening dialog
      this.style.display = 'block';
      dialogComponent.showDialog();
    } else {
      console.error('Quiz popup: showDialog method not available');
    }
  }

  /**
   * Initialize quiz component if it exists
   */
  initializeQuizComponent() {
    // Store original back button HTML if not already stored
    if (!this.originalBackButtonHTML) {
      const backButton = document.querySelector('[data-quiz-back]');
      if (backButton) {
        this.originalBackButtonHTML = backButton.innerHTML;
      }
    }
    
    const quizComponent = this.dynamicContent.querySelector('quiz-component');
    if (quizComponent) {
      // Listen for quiz completion - show results with email capture at top
      quizComponent.addEventListener('quiz:complete', async (event) => {
        const { result, selections, title } = event.detail;
        
        // Remove loading state from submit button
        const submitButton = quizComponent.querySelector('[data-quiz-submit]');
        if (submitButton) {
          submitButton.removeAttribute('aria-busy');
          submitButton.removeAttribute('disabled');
          submitButton.classList.remove('loading');
          if (submitButton.dataset.originalText) {
            submitButton.textContent = submitButton.dataset.originalText;
          }
        }
        
        if (result) {
          if (result.url && this.quizUrl) {
            // Load result from URL if using URL-based loading
            await this.getQuizResult(result.url, title, selections);
          } else {
            // Render result directly from data with email capture at top
            await this.renderer.renderQuizResult(result, title, selections);
          }
        }
      });
      
      // Listen for no results
      quizComponent.addEventListener('quiz:no-results', () => {
        // Remove loading state from submit button
        const submitButton = quizComponent.querySelector('[data-quiz-submit]');
        if (submitButton) {
          submitButton.removeAttribute('aria-busy');
          submitButton.removeAttribute('disabled');
          submitButton.classList.remove('loading');
          if (submitButton.dataset.originalText) {
            submitButton.textContent = submitButton.dataset.originalText;
          }
        }
        
        this.renderer.renderNoResults();
      });
      
      // Initialize if not already initialized, or re-initialize if HTML was restored
      const quizComponentAny = /** @type {any} */ (quizComponent);
      if (typeof quizComponentAny.connectedCallback === 'function') {
        // If component was already initialized but HTML was restored, re-initialize
        if (quizComponentAny.dataset?.initialized === 'true') {
          // Re-initialize to reload result data from script tag
          if (typeof quizComponentAny.initializeQuiz === 'function') {
            quizComponentAny.initializeQuiz();
          } else if (typeof quizComponentAny.loadResultData === 'function') {
            // Try to reload result data if method exists
            quizComponentAny.loadResultData();
          } else {
            // Fallback: call connectedCallback again
            quizComponentAny.connectedCallback();
          }
        } else {
          // First time initialization
          quizComponentAny.connectedCallback();
          quizComponentAny.dataset.initialized = 'true';
        }
      }
    }
  }

  /**
   * Render email capture step before showing results
   */
  async renderEmailCapture(result, selections, title) {
    const emailHeading = this.dataset.emailHeading || 'Your results are almost ready...';
    const emailMessage = this.dataset.emailMessage || 'We will send your personalized Lux Unfiltered routine straight to your inbox - plus 10 percent off your first order.';
    const skipButtonText = this.dataset.skipButtonText || 'SKIP';
    const klaviyoPublicKey = this.dataset.klaviyoPublicKey || '';
    const klaviyoListId = this.dataset.klaviyoListId || '';
    
    // Preload products in the background while showing email form
    const productsPromise = result.products ? this.renderer.preloadProducts(result.products) : Promise.resolve([]);
    
    // Render inline email form (no need to fetch via API - avoids 404 errors)
    this.dynamicContent.innerHTML = `
      <div class="quiz-modal__email-capture">
        <h2 class="quiz-modal__email-heading">${this.renderer.escapeHtml(emailHeading)}</h2>
        <p class="quiz-modal__email-message">${this.renderer.escapeHtml(emailMessage)}</p>
        <form class="quiz-modal__email-form" id="quiz-email-form" data-quiz-email-form novalidate>
          <label for="quiz-email-input" class="visually-hidden">Email (optional)</label>
          <input
            type="email"
            id="quiz-email-input"
            name="email"
            class="quiz-modal__email-input"
            placeholder="EMAIL (OPTIONAL)"
            autocomplete="email"
            autocorrect="off"
            autocapitalize="off"
          >
          <button
            type="submit"
            class="quiz-modal__button quiz-modal__button--email-submit"
            data-quiz-email-submit
          >
            ${this.renderer.escapeHtml(skipButtonText)}
          </button>
          <div class="quiz-modal__email-error" role="alert" hidden></div>
        </form>
      </div>
    `;
    
    // Store result data for after email submission
    this.pendingResult = { result, selections, title, productsPromise };
    
    // Show back button and setup handler
    const backButton = document.querySelector('[data-quiz-back]');
    if (backButton && backButton instanceof HTMLElement) {
      backButton.style.display = 'flex';
      
      // Remove any existing listeners by cloning and replacing to prevent duplicates
      const backButtonAny = /** @type {any} */ (backButton);
      if (backButtonAny.parentNode) {
        const newBackButton = backButton.cloneNode(true);
        backButtonAny.parentNode.replaceChild(newBackButton, backButton);
        
        // Add click handler to go back to quiz
        newBackButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.goBackToQuiz();
          return false;
        });
      } else {
        // If no parent, just add listener directly
        backButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.goBackToQuiz();
          return false;
        });
      }
    }
    
    // Setup email form handler
    const emailForm = this.dynamicContent.querySelector('[data-quiz-email-form]');
    const emailInput = this.dynamicContent.querySelector('#quiz-email-input');
    const emailSubmitButton = this.dynamicContent.querySelector('[data-quiz-email-submit]');
    const errorElement = this.dynamicContent.querySelector('.quiz-modal__email-error');
    
    // Reset submission flag when email form is rendered
    this.isEmailSubmitting = false;
    
    if (emailForm && emailInput && emailSubmitButton) {
      // Store original button text
      const originalButtonText = emailSubmitButton.textContent.trim();
      
      // Update button text based on email input
      emailInput.addEventListener('input', (e) => {
        if (this.isEmailSubmitting) return; // Don't update if submitting
        
        const email = e.target.value.trim();
        if (email) {
          emailSubmitButton.textContent = 'CONTINUE';
        } else {
          emailSubmitButton.textContent = skipButtonText;
        }
        // Hide error when user types
        if (errorElement) {
          errorElement.hidden = true;
        }
      });
      
      // Handle button click directly to prevent multiple clicks
      emailSubmitButton.addEventListener('click', (e) => {
        if (this.isEmailSubmitting) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      });
      
      // Handle form submission - submit to Klaviyo with custom properties
      emailForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Prevent double submission
        if (this.isEmailSubmitting) {
          return;
        }
        this.isEmailSubmitting = true;
        
        const email = emailInput.value.trim();
        
        // Set loading state immediately
        emailSubmitButton.setAttribute('aria-busy', 'true');
        emailSubmitButton.setAttribute('disabled', '');
        emailSubmitButton.classList.add('loading');
        emailSubmitButton.innerHTML = '<span class="quiz-modal__button-loading"></span>';
        
        try {
          // Submit email to Klaviyo if provided (don't block on failure)
          if (email) {
            try {
              await this.renderer.submitQuizEmailToKlaviyo(email, selections, klaviyoPublicKey, klaviyoListId);
            } catch (error) {
              // Log but don't block - proceed to results anyway
              console.warn('Email submission failed, proceeding to results:', error.message);
            }
          }
          
          // Wait for products to finish loading
          await productsPromise;
          
          // Show results
          if (this.pendingResult) {
            this.renderer.renderQuizResult(this.pendingResult.result, this.pendingResult.title);
            this.pendingResult = null;
          }
          
          // Clear stored quiz HTML since we're moving to results
          this.quizHTMLBeforeEmail = null;
        } catch (error) {
          console.error('Error processing email submission:', error);
          // Reset button state on error
          this.isEmailSubmitting = false;
          emailSubmitButton.removeAttribute('aria-busy');
          emailSubmitButton.removeAttribute('disabled');
          emailSubmitButton.classList.remove('loading');
          emailSubmitButton.textContent = originalButtonText;
        }
      });
    }
  }

  /**
   * Go back to quiz from email capture page
   */
  goBackToQuiz() {
    // Clear pending result since we're going back
    this.pendingResult = null;
    
    // Restore quiz HTML if we have it stored
    if (this.quizHTMLBeforeEmail) {
      this.dynamicContent.innerHTML = this.quizHTMLBeforeEmail;
      this.quizHTMLBeforeEmail = null; // Clear stored HTML
      
      // Re-initialize quiz component
      this.initializeQuizComponent();
      
      // Hide back button (quiz component will manage its visibility)
      const backButton = document.querySelector('[data-quiz-back]');
      if (backButton && backButton instanceof HTMLElement) {
        backButton.style.display = 'none';
      }
    } else if (this.originalQuizHTML) {
      // Fallback: restore original quiz HTML
      this.dynamicContent.innerHTML = this.originalQuizHTML;
      this.preloaded = true;
      this.initializeQuizComponent();
      
      // Hide back button
      const backButton = document.querySelector('[data-quiz-back]');
      if (backButton && backButton instanceof HTMLElement) {
        backButton.style.display = 'none';
      }
    }
  }

  /**
   * Handle quiz restart - resets to initial state
   * Consolidated handler to avoid duplicate code
   */
  handleQuizRestart() {
    // Reset back button to normal state (remove retake styling and restore original HTML)
    const backButton = document.querySelector('[data-quiz-back]');
    if (backButton) {
      backButton.classList.remove('quiz-modal__back--retake');
      // Restore original back button HTML
      if (this.originalBackButtonHTML) {
        backButton.innerHTML = this.originalBackButtonHTML;
      }
    }
    
    // Restore original quiz HTML
    if (this.originalQuizHTML) {
      this.dynamicContent.innerHTML = this.originalQuizHTML;
      this.preloaded = true;
      // Show back button again (will be hidden/shown by quiz component based on question)
      if (backButton) {
        backButton.style.display = '';
      }
      // Re-initialize the quiz component
      this.initializeQuizComponent();
    } else if (this.quizUrl) {
      // If using URL-based loading, reload content
      this.preloaded = false;
      this.getQuizContent();
    } else {
      // Fallback: reload the page section
      location.reload();
    }
  }

  /**
   * Fetch quiz content via Sections API
   */
  async getQuizContent() {
    if (this.preloaded && this.dynamicContent.innerHTML.trim() !== '') return;
    
    try {
      // Build URL for section rendering
      const url = new URL(this.quizUrl, window.location.origin);
      
      // Get section HTML using sectionRenderer
      const sectionId = 'quiz-content';
      const html = await sectionRenderer.getSectionHTML(
        sectionId,
        false, // Don't use cache for dynamic content
        url
      );
      
      if (!html) {
        throw new Error(`Quiz content not found for URL: ${this.quizUrl}`);
      }
      
      this.dynamicContent.innerHTML = html;
      this.preloaded = true;
      
      // Initialize quiz component
      this.initializeQuizComponent();
    } catch (error) {
      console.error('Error loading quiz content:', error);
      this.dynamicContent.innerHTML = `
        <div class="quiz-error">
          <p>Error loading quiz. Please try again.</p>
        </div>
      `;
    }
  }

  /**
   * Load quiz result page after quiz completion
   * @param {string} resultUrl - URL to load result from
   * @param {string} title - Result title
   * @param {Array} selections - User's quiz selections (for email capture)
   */
  async getQuizResult(resultUrl, title, selections = []) {
    this.preloaded = false;
    
    try {
      // Build URL for section rendering
      const url = new URL(resultUrl, window.location.origin);
      
      // Get section HTML using sectionRenderer
      const sectionId = 'quiz-result';
      const html = await sectionRenderer.getSectionHTML(
        sectionId,
        false, // Don't use cache for dynamic content
        url
      );
      
      if (!html) {
        throw new Error(`Quiz result not found for URL: ${resultUrl}`);
      }
      
      this.dynamicContent.innerHTML = html;
      
      // Store selections for email submission
      this.renderer.pendingSelections = selections;
      
      // Setup email form if it exists
      const klaviyoPublicKey = this.dataset.klaviyoPublicKey || '';
      const klaviyoListId = this.dataset.klaviyoListId || '';
      this.renderer.setupEmailForm(klaviyoPublicKey, klaviyoListId);
      
      // Setup "Add all to cart" button if products exist
      const resultComponent = this.dynamicContent.querySelector('quiz-result-component');
      if (resultComponent) {
        const products = Array.from(resultComponent.querySelectorAll('[data-product-handle]'));
        if (products.length > 0) {
          const productData = products.map(product => ({
            handle: product.dataset.productHandle,
            id: product.dataset.productId
          }));
          this.renderer.setupAddAllToCartButton(productData);
        }
        
        // Handle restart button
        this.renderer.setupRestartButton(resultComponent);
        
        // Initialize if not already initialized
        if (typeof resultComponent.connectedCallback === 'function' && !resultComponent.dataset.initialized) {
          resultComponent.connectedCallback();
          resultComponent.dataset.initialized = 'true';
        }
      }
    } catch (error) {
      console.error('Error loading quiz result:', error);
      this.dynamicContent.innerHTML = `
        <div class="quiz-error">
          <p>Error loading quiz result. Please try again.</p>
        </div>
      `;
    }
  }
}

// Register the custom element
if (!customElements.get('quiz-popup-stub')) {
  customElements.define('quiz-popup-stub', QuizPopupStub);
}

